import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Copy, Download, Volume2, Loader2, ArrowLeftRight, ClipboardPaste, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { getLanguageName } from '@/lib/languages';
import LanguageSelector from './LanguageSelector';
import { supabase } from '@/integrations/supabase/client';
import AccessGateModal from './AccessGateModal';
import type { AccessState } from '@/hooks/useMembershipStatus';
import { useLanguage } from '@/i18n/LanguageContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import OutputModeSelector, { type OutputMode } from './OutputModeSelector';

type ToneOption = 'standard' | 'formal' | 'casual' | 'business' | 'friendly';

interface TextTranslationProps {
  accessState: AccessState;
}

const TextTranslation = ({ accessState }: TextTranslationProps) => {
  const [sourceLanguage, setSourceLanguage] = useState<string>('auto');
  const [targetLanguage, setTargetLanguage] = useState<string>('en');
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [showGateModal, setShowGateModal] = useState(false);
  const [tone, setTone] = useState<ToneOption>('standard');
  const [outputMode, setOutputMode] = useState<OutputMode>('text');
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  const { t } = useLanguage();
  const lastTranslatedRef = useRef('');
  const translatorRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const wordCount = sourceText.trim().split(/\s+/).filter(Boolean).length;
  const charCount = sourceText.length;
  const canTranslate = accessState === 'active' || accessState === 'trial';
  const hasCompletedTranslation = translatedText.trim().length > 0;
  const hasResettableContent = sourceText.trim().length > 0 || translatedText.trim().length > 0;
  const shouldShowResetButton = hasCompletedTranslation || sourceText.trim().length > 0;
  const requiresResetConfirmation = sourceText.trim().length >= 300 || translatedText.trim().length >= 300;
  const translationSignature = `${sourceLanguage}|${targetLanguage}|${tone}|${sourceText.trim()}`;

  const handleResetNow = useCallback(() => {
    requestIdRef.current += 1;
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSourceText('');
    setTranslatedText('');
    setDetectedLanguage(null);
    setLoading(false);
    setSpeaking(false);
    setShowResetConfirm(false);
    lastTranslatedRef.current = '';
  }, []);

  const requestReset = useCallback(() => {
    if (!hasResettableContent) return;
    if (requiresResetConfirmation) {
      setShowResetConfirm(true);
      return;
    }
    handleResetNow();
  }, [handleResetNow, hasResettableContent, requiresResetConfirmation]);

  const doTranslate = useCallback(async () => {
    if (loading) return;
    if (!canTranslate) {
      setShowGateModal(true);
      return;
    }
    if (!sourceText.trim()) {
      toast.error(t('text.enterText'));
      return;
    }
    if (translationSignature === lastTranslatedRef.current) return;

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: {
          text: sourceText,
          sourceLanguage: sourceLanguage === 'auto' ? 'auto-detect' : sourceLanguage,
          targetLanguage,
          tone: tone !== 'standard' ? tone : undefined,
          outputMode,
        },
      });
      if (currentRequestId !== requestIdRef.current) return;
      if (error) throw error;
      if (data?.error) {
        if (data.error === 'MEMBERSHIP_PENDING') toast.error(t('membership.pending'));
        else if (data.error === 'MEMBERSHIP_BLOCKED') toast.error(t('membership.blocked'));
        else throw new Error(data.error);
        return;
      }
      setTranslatedText(data.translatedText);
      setDetectedLanguage(data.detectedLanguage || null);
      lastTranslatedRef.current = translationSignature;
      toast.success(t('text.translationComplete'));
    } catch (error: any) {
      const errorMsg = error.message || error.error || JSON.stringify(error);
      if (errorMsg.toLowerCase().includes('trial')) toast.error(t('text.trialEnded'));
      else toast.error('Translation failed. Please try again.');
    } finally {
      if (currentRequestId !== requestIdRef.current) return;
      setLoading(false);
    }
  }, [canTranslate, loading, sourceText, sourceLanguage, targetLanguage, t, tone, translationSignature]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((!event.ctrlKey && !event.metaKey) || event.key.toLowerCase() !== 'n') return;
      const activeElement = document.activeElement;
      if (!translatorRef.current || !activeElement || !translatorRef.current.contains(activeElement)) return;
      event.preventDefault();
      requestReset();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [requestReset]);

  const handleSwap = () => {
    if (sourceLanguage === 'auto' || !translatedText) return;
    const prevSource = sourceLanguage;
    const prevTarget = targetLanguage;
    setSourceLanguage(prevTarget);
    setTargetLanguage(prevSource);
    setSourceText(translatedText);
    setTranslatedText(sourceText);
    setDetectedLanguage(null);
    lastTranslatedRef.current = '';
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setSourceText(text);
      toast.success(t('text.pastedFromClipboard'));
    } catch {
      toast.error('Clipboard access denied');
    }
  };

  const handleCopy = () => { navigator.clipboard.writeText(translatedText); toast.success(t('text.copiedToClipboard')); };
  const handleDownload = () => {
    const blob = new Blob([translatedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'translation.txt'; a.click();
    URL.revokeObjectURL(url);
    toast.success(t('text.downloadedSuccessfully'));
  };
  const handleSpeak = () => {
    if ('speechSynthesis' in window) {
      setSpeaking(true);
      const utterance = new SpeechSynthesisUtterance(translatedText);
      utterance.lang = targetLanguage;
      utterance.onend = () => setSpeaking(false);
      speechSynthesis.speak(utterance);
    } else { toast.error(t('text.ttsNotSupported')); }
  };

  return (
    <div ref={translatorRef} className="rounded-xl border bg-card shadow-lg p-4 sm:p-6 space-y-4">
      {/* Language selectors row with swap */}
      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs sm:text-sm">{t('text.sourceLanguage')}</Label>
          <LanguageSelector
            value={sourceLanguage}
            onValueChange={(v) => { setSourceLanguage(v); setDetectedLanguage(null); }}
            includeAutoDetect
          />
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={handleSwap}
          disabled={sourceLanguage === 'auto' || !translatedText}
          className="shrink-0 mb-0.5 h-10 w-10 sm:h-9 sm:w-9 hover:scale-105 transition-transform"
          title={t('text.swap')}
        >
          <ArrowLeftRight className="h-4 w-4" />
        </Button>

        <div className="flex-1 space-y-1.5">
          <Label className="text-xs sm:text-sm">{t('text.targetLanguage')}</Label>
          <LanguageSelector value={targetLanguage} onValueChange={setTargetLanguage} />
        </div>
      </div>

      {/* Tone selector */}
      <div className="space-y-1.5">
        <Label className="text-xs sm:text-sm">{t('text.tone')}</Label>
        <Select value={tone} onValueChange={(v) => setTone(v as ToneOption)}>
          <SelectTrigger className="w-full md:w-48 h-10 sm:h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="standard">{t('text.toneStandard')}</SelectItem>
            <SelectItem value="formal">{t('text.toneFormal')}</SelectItem>
            <SelectItem value="casual">{t('text.toneCasual')}</SelectItem>
            <SelectItem value="business">{t('text.toneBusiness')}</SelectItem>
            <SelectItem value="friendly">{t('text.toneFriendly')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <OutputModeSelector value={outputMode} onValueChange={setOutputMode} voiceOnlyDisabled={!translatedText.trim()} />

      {/* Input / Output panels — stacks vertically on mobile */}
      <div className="grid md:grid-cols-2 gap-3 md:gap-0">
        {/* Source panel */}
        <div className="rounded-lg md:rounded-r-none bg-background border border-border/50 p-4 sm:p-5 space-y-3 min-h-[240px] sm:min-h-[300px]">
          <div className="flex items-center justify-between flex-wrap gap-1">
            <Label className="text-xs sm:text-sm">{t('text.text')}</Label>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handlePaste} className="h-8 sm:h-7 px-2.5 sm:px-2 text-xs gap-1">
                <ClipboardPaste className="h-3.5 w-3.5" />
                {t('text.paste')}
              </Button>
              <span className="text-[10px] sm:text-xs text-muted-foreground">
                {wordCount} {t('text.words')} · {charCount} {t('text.characters')}
              </span>
            </div>
          </div>
          <Textarea
            placeholder={t('text.placeholder')}
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            className="min-h-[160px] sm:min-h-[200px] resize-none text-sm sm:text-base leading-relaxed"
          />
        </div>

        {/* Output panel */}
        <div className="rounded-lg md:rounded-l-none bg-muted/10 border border-border p-4 sm:p-5 space-y-3 min-h-[240px] sm:min-h-[300px]">
          <div className="flex items-center justify-between flex-wrap gap-1">
            <div className="flex items-center gap-2">
              <Label className="text-xs sm:text-sm">{t('text.translation')}</Label>
              {detectedLanguage && sourceLanguage === 'auto' && (
                <Badge variant="secondary" className="text-[10px] sm:text-xs font-normal">
                  {t('text.detectedLanguage').replace('{lang}', getLanguageName(detectedLanguage))}
                </Badge>
              )}
            </div>
            {shouldShowResetButton && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={requestReset}
                      className="hidden md:inline-flex gap-1.5 text-xs h-8 px-3 animate-in fade-in-0 zoom-in-95"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      {t('text.newTranslation')}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('text.newTranslationTooltip')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="relative">
            <Textarea value={outputMode === 'voice' ? '' : translatedText} readOnly placeholder={outputMode === 'voice' ? t('voice.audioOnlyReady') : t('text.translationPlaceholder')} className="min-h-[160px] sm:min-h-[200px] resize-none bg-muted/30 text-sm sm:text-base leading-relaxed" />
          {(translatedText || sourceText.trim()) && (
            <div className="flex items-center gap-2 pt-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={requestReset} className="gap-1.5 text-xs h-9 sm:h-8 px-3 md:hidden animate-in fade-in-0 zoom-in-95">
                <RotateCcw className="h-3.5 w-3.5" />
                {t('text.newTranslation')}
              </Button>
              {outputMode !== 'voice' && <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5 text-xs h-9 sm:h-8 px-3 hover:scale-105 transition-transform">
                <Copy className="h-3.5 w-3.5" />
                {t('text.copy')}
              </Button>}
              <Button variant="outline" size="sm" onClick={handleSpeak} disabled={speaking} className="gap-1.5 text-xs h-9 sm:h-8 px-3 hover:scale-105 transition-transform">
                <Volume2 className="h-3.5 w-3.5" />
                {speaking ? t('text.speaking') : t('text.speak')}
              </Button>
              {outputMode !== 'voice' && <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5 text-xs h-9 sm:h-8 px-3 hover:scale-105 transition-transform">
                <Download className="h-3.5 w-3.5" />
                {t('text.download')}
              </Button>}
            </div>
          )}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Button onClick={doTranslate} disabled={loading || !canTranslate || !sourceText.trim()} size="lg" className="w-full text-sm sm:text-base h-12 hover:scale-[1.02] transition-transform">
          {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('text.translating')}</>) : t('text.translate')}
        </Button>
        {shouldShowResetButton && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" onClick={requestReset} className="w-full sm:w-auto h-12 px-4 gap-2 animate-in fade-in-0 zoom-in-95">
                  <RotateCcw className="h-4 w-4" />
                  {t('text.newTranslation')}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('text.newTranslationTooltip')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('text.resetConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('text.resetConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('text.keepEditing')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetNow}>{t('text.clearAll')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AccessGateModal open={showGateModal} onOpenChange={setShowGateModal} accessState={accessState} />
    </div>
  );
};

export default TextTranslation;
